import axios from 'axios';
import moment from 'moment';
import i18next from 'i18next';
import logger from './logger';
import { setUser } from './../utils/actions';

let store
/** Shared in-flight /dir/login/refresh. Concurrent callers join this promise. */
let inFlightRefresh = null;
/** After a failed refresh, wait this long before trying again (token still valid). */
const REFRESH_RETRY_COOLDOWN_MS = 30000;
let lastFailedRefreshAt = 0;

export const injectStore = _store => {
  store = _store
  inFlightRefresh = null;
  lastFailedRefreshAt = 0;
}

/**
 * Prefer Redux user so session checks see tokens/loginTimeStamp written by
 * refreshToken(), even when the caller still holds a stale snapshot.
 */
function getSessionUser(user) {
  try {
    const storeUser = store && store.getState && store.getState().user;
    if (storeUser && storeUser.username) {
      return storeUser;
    }
  } catch (e) {
    // store not injected yet
  }
  return user;
}

function persistRefreshedUser(user, authenticationResult) {
  if (!authenticationResult || !user || typeof global.updateUserObject !== 'function') {
    return;
  }
  try {
    if (store && store.dispatch) {
      store.dispatch(
        setUser(global.updateUserObject(getSessionUser(user), authenticationResult))
      );
    }
  } catch (e) {
    console.error('Error updating user after token refresh', e);
  }
}

function sessionElapsedSeconds(sessionUser) {
  return moment.duration(
    moment().diff(moment(sessionUser.loginTimeStamp))
  ).asSeconds();
}

function isInProactiveRefreshWindow(sessionUser) {
  const elapsed = sessionElapsedSeconds(sessionUser);
  const expiresIn = Number(sessionUser.expiresIn);
  return elapsed < expiresIn && elapsed > expiresIn - 1800;
}

function isRefreshCooldownActive() {
  if (!lastFailedRefreshAt) return false;
  return Date.now() - lastFailedRefreshAt < REFRESH_RETRY_COOLDOWN_MS;
}

function shouldAttemptRefresh(sessionUser) {
  return isInProactiveRefreshWindow(sessionUser) && !isRefreshCooldownActive();
}

function authenticationResultFromUser(sessionUser) {
  return {
    AuthenticationResult: {
      IdToken: sessionUser.idToken,
      AccessToken: sessionUser.accessToken,
      ExpiresIn: sessionUser.expiresIn,
      TokenType: sessionUser.tokenType,
      RefreshToken: sessionUser.refreshToken,
    },
  };
}

function decodeJwtExpMs(idToken) {
  if (!idToken || typeof idToken !== 'string') return null;
  const parts = idToken.split('.');
  if (parts.length < 2) return null;
  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    if (typeof payload.exp !== 'number') return null;
    return payload.exp * 1000;
  } catch (e) {
    return null;
  }
}

/** True when the current ID token can still authorize APIs (JWT exp, else client clock). */
function isCurrentTokenStillValid(sessionUser) {
  if (!sessionUser) return false;
  const jwtExpMs = decodeJwtExpMs(sessionUser.idToken);
  if (jwtExpMs != null) {
    return jwtExpMs > Date.now() + 5000;
  }
  return sessionElapsedSeconds(sessionUser) < Number(sessionUser.expiresIn);
}

/**
 * Refresh failed but the ID token is still inside the 1-hour window.
 * Start a 30s cooldown so waiters retry APIs with the current JWT instead of
 * looping immediately; the next API after the cooldown can refresh again.
 */
function keepSessionAfterFailedRefresh(sessionUser) {
  lastFailedRefreshAt = Date.now();
  return authenticationResultFromUser(getSessionUser(sessionUser) || sessionUser);
}

/**
 * Client session clock is loginTimeStamp + expiresIn. Reset that stamp only on
 * login or a successful token refresh — not on every API response, or the
 * last-30-minute refresh window never opens and Cognito JWTs die at ~1 hour.
 */
function shouldBlockForSession(user, callback) {
  const sessionUser = getSessionUser(user);
  if (!sessionUser || !sessionUser.username) {
    return false;
  }
  const totalLoginDurationSec = sessionElapsedSeconds(sessionUser);
  console.log('Session Active from (Sec): ', totalLoginDurationSec);
  if (shouldAttemptRefresh(sessionUser)) {
    // Last 30 minutes of the token window: caller should refresh then retry.
    console.log('refresh called');
    callback && callback('Refresh Token Needed', null);
    return true;
  }
  if (totalLoginDurationSec > Number(sessionUser.expiresIn)) {
    console.log('session expired called');
    callback && callback('Session Expired', null);
    return true;
  }
  return false;
}

/**
 * If a refresh is already in flight, wait for it then re-check the session and
 * continue the original request. Avoids a second /login/refresh from a later API.
 */
function runWithSession(user, callback, proceed) {
  const start = () => {
    if (shouldBlockForSession(user, callback)) {
      return;
    }
    proceed();
  };
  if (inFlightRefresh) {
    inFlightRefresh.then(start).catch(() => {
      // Hard refresh failure already redirected to /signin.
    });
    return;
  }
  start();
}

function startTokenRefresh(url, sessionUser) {
  return axios({
    method: 'post',
    url,
    data: { refreshToken: sessionUser.refreshToken },
    headers: { authorization: `${sessionUser.tokenType} ${sessionUser.idToken}` },
  }).then((responseData) => {
    console.log(
      'Refresh Token POST Response: ' + JSON.stringify(responseData)
    );
    const payload = responseData?.data;
    lastFailedRefreshAt = 0;
    persistRefreshedUser(sessionUser, payload?.AuthenticationResult);
    return payload;
  });
}

// Add a response interceptor
axios.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    const regex = new RegExp('Network.*Error.*');
    if (error && error.message) {
      if (regex.test(error.message)) {
        // check whether back-end is down else throw the network error
        let isServerDown = false;
        try {
          const res = await fetch(global.URL_Health, { method: 'GET' });
          console.log('Response status', res.status);
          if (res.status === 502 || res.status === 503 || res.status === 504) {
            isServerDown = true;
          }
        } catch (error) {
          console.error("Fetch error:", error);
          isServerDown = true;
        }
        if(isServerDown) {
          console.log("Back end API returns error.");
          let e = {
            response: {
              data: {
                errorMessage:
                  'The service is currently down for maintenance. Please try again later.',
              },
            },
          };
          return Promise.reject(e);
        }

        logger.error('Network Error :  %o', error);
        // custom, message
        // when request timed out Serverless return 504 without any other information
        // we should fix in serverless itself
        let e = {
          response: {
            data: {
              errorMessage:
                'Network Error : Request timed out. Please try again later.',
            },
          },
        };
        return Promise.reject(e);
      }
    }
    logger.error('http errors: %o', error);
    return Promise.reject(error);
  }
);

function parseErrorCode(error) {
  //error.response.data.errorCode? error.response.data.errorCode : null;
  if (
    error &&
    error.response &&
    error.response.data &&
    error.response.data.errorCode
  ) {
    return {
      errorCode: error.response.data.errorCode,
      errorMessage: error.response.data.errorMessage
        ? error.response.data.errorMessage
        : error.response.data.message,
    };
  } else if (
    error &&
    error.response &&
    error.response.data &&
    error.response.data
  ) {
    return {
      errorCode: error.response.data.errorCode,
      errorMessage: error.response.data.errorMessage
        ? error.response.data.errorMessage
        : error.response.data.message,
    };
  }
  return { errorCode: '', errorMessage: '' };
}
let http = {
  get: (url, payload, headers, showLoader, user, callback) => {
    runWithSession(user, callback, () => {
    
    showLoader?.(true);
    console.log('GET Request: ' + url);
    let headersExtra = {};
    if (user && user.ip) {
      headersExtra = {
        'x-matson-ip': user.ip,
        'x-matson-city': user.city,
        'x-matson-lang': user.lang,
        'x-matson-country': user.country,
        'x-matson-session': user.sessionId,
      };
    }
    const requestHeaders = headers?.headers ?? headers ?? {};
    const axiosConfig = {
      params: payload,
      headers: { ...requestHeaders, ...headersExtra },
    };

    if (headers?.signal) {
      axiosConfig.signal = headers.signal;
    }

    axios
      .get(url, axiosConfig)
      .then((responseData) => {
        console.log('GET Response: ' + JSON.stringify(responseData));
        showLoader?.(false);
        if (responseData) {
          callback && callback(responseData.data, null);
        }
      })
      .catch((error) => {
        showLoader?.(false);
        if (axios.isCancel(error)) {
          console.log('Request canceled', error.message);
          return;
        }
        console.log('ERROR', error);
        const { errorCode, errorMessage } = parseErrorCode(error);
        let message = '';
        if (errorCode) {
          let output = {};
          const messageArgs = error.response.data.messageArgs
            ? error.response.data.messageArgs
            : null;
          if (messageArgs) {
            messageArgs.forEach((value, index) => {
              output['key' + index] = value;
            });
          }
          message = i18next.t('common:' + errorCode, output);
        } else if (errorMessage) {
          // use legacy logic
          message = error.response.data.errorMessage
            ? error.response.data.errorMessage
            : error.response.data.message
            ? error.response.data.message.replace(/\s*\[.*?\]\s*/g, '')
            : 'Something went wrong';
        }
        if (!message) {
          message = 'Something went wrong';
        }
        callback && callback(null, message, error.response?.data);
      });
    });
  },
  post: (url, data, headers, showLoader, user, callback) => {
    runWithSession(user, callback, () => {
    showLoader(true);
    console.log('POST Request: ' + url);
    let headersExtra = {};
    if (user && user.ip) {
      headersExtra = {
        'x-matson-ip': user.ip,
        'x-matson-city': user.city,
        'x-matson-lang': user.lang,
        'x-matson-country': user.country,
        'x-matson-session': user.sessionId,
      };
    }
    axios
      .post(url, data, { headers: { ...headers.headers, ...headersExtra } })
      .then((responseData) => {
        console.log('POST Response: ' + JSON.stringify(responseData));
        showLoader(false);
        // if (responseData) {
        callback && callback(responseData.data, null);
        // }
      })
      .catch((error) => {
        showLoader(false);
        if (axios.isCancel(error)) {
          console.log('Request canceled', error.message);
          return;
        }
        // console.log(JSON.stringify(error));
        console.log('Error', error);
        // error.response.data
        if (
          error.response.data.message ===
            '[401] PreAuthentication failed with error Password Expired.' ||
          error.response.data.errorMessage ===
            '[401] PreAuthentication failed with error Password Expired.'
        ) {
          // alert('Password Expired, Please reset it');
          callback && callback('Reset Password', null);
        } else if (
          error.response.data.message ===
            '[401] Password reset required for the user' ||
          error.response.data.errorMessage ===
            '[401] Password reset required for the user'
        ) {
          callback &&
            callback(
              'Direct Reset Password',
              'Password reset required for the user'
            );
        } else if (
          error.response.data.message === '[401] User is not confirmed.' ||
          error.response.data.errorMessage === '[401] User is not confirmed.'
        ) {
          callback &&
            callback(
              'User not confirmed',
              'Account not yet verified with 6 digit code.'
            );
        } else {
          const errorCode = error.response.data.errorCode
            ? error.response.data.errorCode
            : null;
          let message = '';
          if (errorCode) {
            let output = {};
            const messageArgs = error.response.data.messageArgs
              ? error.response.data.messageArgs
              : null;
            if (messageArgs) {
              messageArgs.forEach((value, index) => {
                output['key' + index] = value;
              });
            }
            message = i18next.t('common:' + errorCode, output);
          } else {
            // use legacy logic
            message = error.response.data.errorMessage
              ? error.response.data.errorMessage
              : error.response.data.message
              ? error.response.data.message.replace(/\s*\[.*?\]\s*/g, '')
              : 'Something went wrong';
          }
          if (!message) {
            message = 'Something went wrong';
          }
          callback && callback(null, message, error.response?.data);

          // alert(error.response.data.message);
        }
        //alert(error.message);
      });
    });
  },
  put: (url, data, headers, showLoader, user, callback) => {
    runWithSession(user, callback, () => {
    showLoader(true);
    console.log('PUT Request: ' + url);
    let headersExtra = {};
    if (user && user.ip) {
      headersExtra = {
        'x-matson-ip': user.ip,
        'x-matson-city': user.city,
        'x-matson-lang': user.lang,
        'x-matson-country': user.country,
        'x-matson-session': user.sessionId,
      };
    }
    axios
      .put(url, data, { ...headers, ...headersExtra })
      .then((responseData) => {
        console.log('PUT Response: ' + JSON.stringify(responseData));
        showLoader(false);
        // if (responseData) {
        callback && callback(responseData.data, null);
        // }
      })
      .catch((error) => {
        showLoader(false);
        if (axios.isCancel(error)) {
          console.log('Request canceled', error.message);
          return;
        }
        // console.log(JSON.stringify(error));
        console.log('ERROR', error);
        // error.response.data
        if (
          error.response.data.message ===
            '[401] PreAuthentication failed with error Password Expired.' ||
          error.response.data.errorMessage ===
            '[401] PreAuthentication failed with error Password Expired.'
        ) {
          // alert('Password Expired, Please reset it');
          callback && callback('Reset Password', null);
        } else if (
          error.response.data.message ===
            '[401] Password reset required for the user' ||
          error.response.data.errorMessage ===
            '[401] Password reset required for the user'
        ) {
          callback &&
            callback(
              'Direct Reset Password',
              'Password reset required for the user'
            );
        } else if (
          error.response.data.message === '[401] User is not confirmed.' ||
          error.response.data.errorMessage === '[401] User is not confirmed.'
        ) {
          callback &&
            callback(
              'User not confirmed',
              'Account not yet verified with 6 digit code.'
            );
        } else {
          const errorCode = error.response.data.errorCode
            ? error.response.data.errorCode
            : null;
          let message = '';
          if (errorCode) {
            let output = {};
            const messageArgs = error.response.data.messageArgs
              ? error.response.data.messageArgs
              : null;
            if (messageArgs) {
              messageArgs.forEach((value, index) => {
                output['key' + index] = value;
              });
            }
            message = i18next.t('common:' + errorCode, output);
          } else {
            // use legacy logic
            message = error.response.data.errorMessage
              ? error.response.data.errorMessage
              : error.response.data.message
              ? error.response.data.message.replace(/\s*\[.*?\]\s*/g, '')
              : 'Something went wrong';
          }
          if (!message) {
            message = 'Something went wrong';
          }
          callback && callback(null, message);
        }
        //alert(error.message);
      });
    });
  },
  delete: (url, data, headers, showLoader, user, callback) => {
    runWithSession(user, callback, () => {
    showLoader(true);
    let headersExtra = {};
    if (user && user.ip) {
      headersExtra = {
        'x-matson-ip': user.ip,
        'x-matson-city': user.city,
        'x-matson-lang': user.lang,
        'x-matson-country': user.country,
        'x-matson-session': user.sessionId,
      };
    }
    console.log('DELETE Request: ' + url, data);
    axios({
      url,
      method: 'delete',
      data,
      headers: { ...headers.headers, ...headersExtra },
    })
      .then((responseData) => {
        console.log('DELETE Response: ' + JSON.stringify(responseData));
        showLoader(false);
        if (responseData) {
          callback && callback(responseData.data, null);
        }
      })
      .catch((error) => {
        showLoader(false);
        if (axios.isCancel(error)) {
          console.log('Request canceled', error.message);
          return;
        }
        console.log('ERROR', error);
        // alert(error.message);
        const errorCode = error.response.data.errorCode
          ? error.response.data.errorCode
          : null;
        let message = '';
        if (errorCode) {
          let output = {};
          const messageArgs = error.response.data.messageArgs
            ? error.response.data.messageArgs
            : null;
          if (messageArgs) {
            messageArgs.forEach((value, index) => {
              output['key' + index] = value;
            });
          }
          message = i18next.t('common:' + errorCode, output);
        } else {
          // use legacy logic
          message = error.response.data.errorMessage
            ? error.response.data.errorMessage
            : error.response.data.message
            ? error.response.data.message.replace(/\s*\[.*?\]\s*/g, '')
            : 'Something went wrong';
        }
        if (!message) {
          message = 'Something went wrong';
        }
        callback && callback(null, message);
      });
    });
  },
  refreshToken: (url, user, showLoader, callback) => {
    // One in-flight refresh; other callers wait and continue with the same result.
    const sessionUser = getSessionUser(user) || user;
    showLoader(true);

    if (!inFlightRefresh && sessionUser && !shouldAttemptRefresh(sessionUser)) {
      showLoader(false);
      callback && callback(authenticationResultFromUser(sessionUser), null);
      return;
    }

    if (!inFlightRefresh) {
      inFlightRefresh = startTokenRefresh(url, sessionUser)
        .catch((error) => {
          console.log('REFRESH error', error);
          const current = getSessionUser(sessionUser) || sessionUser;
          if (isCurrentTokenStillValid(current)) {
            console.log('REFRESH failed but token still valid; keeping session');
            return keepSessionAfterFailedRefresh(current);
          }
          window.location.href = '/signin';
          throw error;
        })
        .finally(() => {
          inFlightRefresh = null;
        });
    }

    inFlightRefresh
      .then((data) => {
        showLoader(false);
        callback && callback(data, null);
      })
      .catch(() => {
        showLoader(false);
      });
  },
};

export default http;
